import { defineStore } from 'pinia';
import axios from 'axios';
import { useMainStore } from '@/stores/useMainStore';
import { reportNonFatalError } from '@/utils/storeErrorReporting';

const API_DOMAIN = import.meta.env.VITE_API_URL;

/**
 * Request Template Store
 *
 * Note on organization scoping:
 * Request templates are platform-wide resources that are not scoped
 * to specific organizations. The templates API endpoints do not accept
 * or require organizationId parameters, as templates are shared across
 * all organizations and filtered by user permissions via the auth token.
 *
 * Note on app mode:
 * Template operations (fetch, create, update, delete) are available to
 * all user modes (educator, employee, admin) without mode-specific logic.
 * The API handles permissions based on the auth token.
 */

export const useRequestTemplateStore = defineStore('requestTemplate', {
    state: () => ({
        templates: [],
        currentTemplate: {},
        recommendedTemplates: [],
        totalCount: null,
        filteredCount: null,
        lastPopulated: null,
    }),
    actions: {
        /**
         * Fetches a single request template by ID
         * @param {Object} payload - Contains token and data.templateId
         */
        async getRequestTemplate(payload) {
            try {
                const response = await axios({
                    method: 'GET',
                    url: `${API_DOMAIN}/templates/${payload.data.templateId}`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                const { data } = response;

                this.currentTemplate = {
                    id: data.template_id,
                    ...data.template_data,
                    creatorId: data.creator_id,
                    isActive: data.is_active,
                    isFeatured: data.is_featured,
                    createdAt: data.created_datetime ? new Date(data.created_datetime) : null,
                };
            } catch (error) {
                reportNonFatalError(error, { title: 'Failed to load request template' });
            }
        },

        /**
         * Fetches paginated list of request templates
         * @param {Object} payload - Contains token and optional data.params for query string
         */
        async getRequestTemplates(payload) {
            const mainStore = useMainStore();
            mainStore.updateLoading({ key: 'page', status: true });

            try {
                const params = payload.data && payload.data.params ? `?${payload.data.params}` : '';

                const response = await axios({
                    method: 'GET',
                    url: `${API_DOMAIN}/templates${params}`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                const records = response.data.records || [];

                const formattedTemplates = records.map((record) => ({
                    id: record.template_id,
                    ...record.template_data,
                    creatorId: record.creator_id,
                    isActive: record.is_active,
                    isFeatured: record.is_featured,
                    createdAt: record.created_datetime ? new Date(record.created_datetime) : null,
                }));

                // Timestamp last time cache was populated.
                this.lastPopulated = new Date();

                // Extract recommended templates (featured templates, max 3)
                const recommended = formattedTemplates.filter((template) => template.isFeatured === true).slice(0, 3);

                // Update pagination
                mainStore.updatePagination(response.data);

                // Update state
                this.templates = formattedTemplates;
                this.recommendedTemplates = recommended;
                this.filteredCount = response.data.record_count;
                this.totalCount = response.data.all_active_templates_count;
            } catch (error) {
                reportNonFatalError(error, { title: 'Failed to load request templates' });
            } finally {
                mainStore.updateLoading({ key: 'page', status: false });
            }
        },

        /**
         * Creates a new request template
         * @param {Object} payload - Contains token and data with templateData and isFeatured
         */
        async createRequestTemplate(payload) {
            const mainStore = useMainStore();
            mainStore.updateLoading({ key: 'page', status: true });

            try {
                await axios({
                    method: 'POST',
                    url: `${API_DOMAIN}/templates`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                        'Content-Type': 'application/json',
                    },
                    data: {
                        template_data: payload.data.templateData,
                        is_featured: payload.data.isFeatured,
                    },
                });
            } catch (error) {
                reportNonFatalError(error, { title: 'Failed to create request template' });
            } finally {
                mainStore.updateLoading({ key: 'page', status: false });
            }
        },

        /**
         * Updates an existing request template
         * @param {Object} payload - Contains token and data with
         * templateId, templateData, and isFeatured
         */
        async updateRequestTemplate(payload) {
            const mainStore = useMainStore();
            mainStore.updateLoading({ key: 'page', status: true });

            try {
                await axios({
                    method: 'POST',
                    url: `${API_DOMAIN}/templates/${payload.data.templateId}`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                        'Content-Type': 'application/json',
                    },
                    data: {
                        template_data: payload.data.templateData,
                        is_featured: payload.data.isFeatured,
                    },
                });
            } catch (error) {
                reportNonFatalError(error, { title: 'Failed to update request template' });
            } finally {
                mainStore.updateLoading({ key: 'page', status: false });
            }
        },

        /**
         * Deletes a request template
         * @param {Object} payload - Contains token and data.templateId
         */
        async deleteRequestTemplate(payload) {
            const mainStore = useMainStore();
            mainStore.updateLoading({ key: 'page', status: true });

            try {
                await axios({
                    method: 'DELETE',
                    url: `${API_DOMAIN}/templates/${payload.data.templateId}`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                        'Content-Type': 'application/json',
                    },
                });
            } catch (error) {
                reportNonFatalError(error, { title: 'Failed to delete request template' });
            } finally {
                mainStore.updateLoading({ key: 'page', status: false });
            }
        },
    },
});
